import { FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import BasicModal from 'components/Common/BasicModal';
import PrimaryButton from 'components/Common/PrimaryButton';
import { getTranslatedDataLanguages } from 'helpers/filters';
import { filterLanguagesByCountry } from 'helpers/languages';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setMessageOutputLanguages } from 'store/global/globalSlice';
import { SelectedLanguageType } from 'store/global/types';
import { LanguagesModalType } from './types';

const LanguagesModal: FC<LanguagesModalType> = ({
    open,
    onClose,
    anchorEl,
    language,
    country,
    messageId,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { messageOutputLanguages, outputLanguages } = useAppSelector((state) => state.global);
    const [countryOutputLanguages, setCountryOutputLanguages] = useState<
        SelectedLanguageType[] | []
    >([]);
    const outputPosition = anchorEl.current?.parentElement?.getBoundingClientRect();

    useEffect(() => {
        if (country && language) {
            setCountryOutputLanguages(filterLanguagesByCountry(country, outputLanguages, language));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputLanguages]);

    useEffect(() => {
        const languages = messageOutputLanguages.find(
            (item) => item.messageId === messageId,
        )?.outputLanguages;
        if (languages) {
            setCountryOutputLanguages((prev) =>
                prev.map((item) => ({
                    ...item,
                    checked: languages?.find((l) => l.language_code === item.language_code)
                        ? true
                        : false,
                })),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCheckboxChange = (languageCode: string, checked: boolean) => {
        setCountryOutputLanguages((prev) =>
            prev.map((language) => ({
                ...language,
                checked: language.language_code === languageCode ? checked : language.checked,
            })),
        );
    };

    const handleSave = () => {
        const selectedLanguages = countryOutputLanguages.filter((l) => l.checked === true);
        dispatch(
            setMessageOutputLanguages({ messageId: messageId, outputLanguages: selectedLanguages }),
        );
        onClose();
    };

    const noCheckedLanguages =
        countryOutputLanguages.filter((l) => l.checked === true).length === 0;
    return (
        <BasicModal
            sx={{
                position: 'absolute',
                bottom: `calc(100vh - ${outputPosition?.bottom}px + 2px)`,
                left: outputPosition?.left ? outputPosition?.left - 10 : undefined,
            }}
            slotProps={{ backdrop: { className: 'output-languages-modal-backdrop' } }}
            open={open}
            onClose={onClose}
        >
            <Box className="output-languages-modal">
                {t('web_app.intake_notes.output_languages')}
                <FormGroup>
                    {getTranslatedDataLanguages(countryOutputLanguages).map((language) => (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!language.checked}
                                    onChange={() =>
                                        handleCheckboxChange(
                                            language.language_code,
                                            !language.checked,
                                        )
                                    }
                                    value={language.id}
                                />
                            }
                            label={language.language_name}
                            key={language.id}
                        />
                    ))}
                </FormGroup>
                <PrimaryButton
                    text={t('web_app.save_btn')}
                    onClick={handleSave}
                    disabled={noCheckedLanguages}
                ></PrimaryButton>
            </Box>
        </BasicModal>
    );
};

export default LanguagesModal;
